import { BuildingIcon, CameraOutlinedIcon } from '@village/icons';
import { useToggle } from '@village/tools';
import { AppointmentCard } from 'components/appointments-card';
import { CenteredSpinner } from 'components/centered-spinner';
import { DatesNavigation } from 'components/dates-navigation';
import { BookingDate, DepartmentField, ReasonField, ReasonOption } from 'components/fields';
import { ReasonSwitchModal } from 'components/modal/reason-switch-modal';
import { VisitTypeBanner } from 'components/visit-type-banner';
import {
    useAppointmentsGroups,
    useAutocompleteScrollHandler,
    useBooking,
    useDepartments,
    useMarkets,
    useProviders,
    useReasons,
    useScrollToTop,
    useSyncBookingWithUrl,
    useIsMobile,
    useLimitedVisitTypeReasonsFlag,
    useSteadyMDDepartmentId,
} from 'hooks';
import { usePrnNextAvailableAppointment } from 'hooks/use-prn-next-available-appointment';
import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { Reason } from 'types';
import { filterReasonsByProviders } from 'utils/reason';

import * as Styled from './styles';

const SelectAppointmentPage = () => {
    useSyncBookingWithUrl();
    useScrollToTop();

    const isMobile = useIsMobile();
    const departmentFieldRef = useRef(null);
    const { setBookingField, booking } = useBooking();
    const { isFetching: isMarketsFetching } = useMarkets();
    const handleAutocompleteScrollOnMobile = useAutocompleteScrollHandler();
    const { reason, previouslySelectedReason, reason: selectedReason } = booking;
    const { data: departments, isFetching: isDepartmentsFetching } = useDepartments(booking);
    const { isFetching: isReasonsFetching, data: allReasons } = useReasons(booking);
    const { data: providers, isFetching: isProvidersFetching } = useProviders(booking);
    const [reasonPending, setPendingReason] = useState<Reason | undefined>(undefined);
    const { appointmentsGroupings, isAppointmentsFetching } = useAppointmentsGroups(providers);
    const { isNextAvailableFetching, availabilityWithoutUnavailablePrnProviders } =
        usePrnNextAvailableAppointment(appointmentsGroupings);
    const steadyMDDeptId = useSteadyMDDepartmentId();

    const {
        value: isReasonSwitchModalOpen,
        switchOff: closeReasonSwitchModal,
        switchOn: openReasonSwitchModal,
    } = useToggle(false);

    const reasons = useMemo(() => {
        if (!booking.department || providers.length === 0 || !allReasons) {
            return [];
        }

        return filterReasonsByProviders(
            allReasons,
            providers.filter(({ isprn }) => !isprn),
            booking.departmentAndRelatedDepartments
        );
    }, [allReasons, booking.department, providers, booking.departmentAndRelatedDepartments]);

    const { inOfficeReasonsOnly, oneVisitTypeOnly } = useLimitedVisitTypeReasonsFlag(reasons);
    const shouldShowSwitchToVideoPrompt = Boolean(
        steadyMDDeptId &&
            reasons.length > 0 &&
            booking.reason?.visitType !== 'VIDEO' &&
            !inOfficeReasonsOnly && //not in person reasons only
            !oneVisitTypeOnly
    );

    const isFetching =
        isAppointmentsFetching || isReasonsFetching || isProvidersFetching || isMarketsFetching || isNextAvailableFetching;

    const handleUpdateReason = useCallback(() => {
        setBookingField({ reason: reasonPending });
    }, [reasonPending, setBookingField]);

    const handleReasonChange = useCallback(
        (option: ReasonOption | null) => {
            const matchedReason = reasons?.find(({ id }) => option?.reasonId === id);

            if (selectedReason?.is_in_person && !matchedReason?.is_in_person) {
                setPendingReason(matchedReason);
                openReasonSwitchModal();
            } else {
                setPendingReason(undefined);
                setBookingField({ reason: matchedReason });
            }
        },
        [reasons, selectedReason?.is_in_person, setBookingField, openReasonSwitchModal]
    );

    const handleSwitchToVideo = useCallback(() => {
        const newReason = reasons.find((reason) => reason.name === selectedReason?.name && reason.is_in_person === false);
        if (newReason) {
            setPendingReason(newReason);
            openReasonSwitchModal();
        }
    }, [reasons, selectedReason]);

    const onReasonSwitchCancel = useCallback(() => {
        setPendingReason(undefined);
        closeReasonSwitchModal();
    }, [closeReasonSwitchModal]);

    const onReasonSwitchConfirm = useCallback(() => {
        handleUpdateReason();
        closeReasonSwitchModal();
    }, [handleUpdateReason, closeReasonSwitchModal]);

    useEffect(() => {
        // When patient goes back to select-appointment page we unselect currently selected appointment
        setBookingField({ appointment: undefined });
    }, [setBookingField]);

    return (
        <Styled.Container>
            <Styled.Header>
                <Styled.FormContainer>
                    <DepartmentField
                        ref={departmentFieldRef}
                        departments={departments}
                        isLoading={isDepartmentsFetching}
                        label="Location:"
                        id="select-department"
                        onFocus={handleAutocompleteScrollOnMobile(departmentFieldRef)}
                    />

                    <ReasonField
                        label="Reason for Appointment:"
                        id="select-reason"
                        onChange={handleReasonChange}
                        reasons={reasons}
                    />

                    {/* on mobile, the date picker is located in the DatesNavigation component */}
                    {!isMobile ? <BookingDate id="select-date" /> : null}
                </Styled.FormContainer>
            </Styled.Header>

            <Styled.NavigationContainer>
                <Styled.NavigationBannerContainer>
                    {booking.reason?.is_in_person ? (
                        <VisitTypeBanner
                            title="You’re scheduling an in-office visit"
                            icon={BuildingIcon}
                            variant="small"
                            status="visit_type"
                        />
                    ) : (
                        <VisitTypeBanner
                            title="You’re scheduling a video visit"
                            icon={CameraOutlinedIcon}
                            variant="small"
                            status="visit_type"
                        />
                    )}
                </Styled.NavigationBannerContainer>
                <DatesNavigation isAppointmentsFetching={isAppointmentsFetching} />
            </Styled.NavigationContainer>
            {shouldShowSwitchToVideoPrompt && (
                <Styled.SwitchToVideoBannerContainer icon={null} status="detail">
                    <Styled.SwitchToVideoGrid>
                        <div>
                            <Styled.SwitchToVideoBannerHeader>Need to be seen sooner?</Styled.SwitchToVideoBannerHeader>
                            <Styled.SwitchToVideoBannerText>
                                We&apos;ve expanded virtual care options to better serve you.&nbsp;
                                <Styled.SwitchToVideoBannerAddonText>
                                    Consider scheduling a video visit if availability is limited.
                                </Styled.SwitchToVideoBannerAddonText>
                            </Styled.SwitchToVideoBannerText>
                        </div>
                        <div>
                            <Styled.ButtonWrapper>
                                <Styled.Button
                                    onClick={handleSwitchToVideo}
                                    size="medium"
                                    variant="secondary"
                                    data-testid="switch-to-video-button"
                                    startIcon={<CameraOutlinedIcon />}
                                >
                                    View video visits
                                </Styled.Button>
                            </Styled.ButtonWrapper>
                        </div>
                    </Styled.SwitchToVideoGrid>
                </Styled.SwitchToVideoBannerContainer>
            )}
            <Styled.RelativeContainer>
                {isFetching ? (
                    <Styled.SpinnerContainer>
                        <Styled.SpinnerWrapper>
                            <CenteredSpinner />
                        </Styled.SpinnerWrapper>
                    </Styled.SpinnerContainer>
                ) : null}
                <Styled.AppointmentsContainer $loading={isFetching}>
                    {availabilityWithoutUnavailablePrnProviders.map((group) => (
                        <AppointmentCard
                            key={`${group.provider.providerid}-${group.department.departmentid}`}
                            appointmentsGroup={group}
                            isAppointmentsFetching={isAppointmentsFetching}
                        />
                    ))}
                </Styled.AppointmentsContainer>
            </Styled.RelativeContainer>

            {!reason && previouslySelectedReason ? (
                <Styled.MissingReasonTitle type="body1">
                    Previously selected appointment reason is not available at this location. Please select a new reason for the
                    appointment or change the location.
                </Styled.MissingReasonTitle>
            ) : null}

            <ReasonSwitchModal open={isReasonSwitchModalOpen} onCancel={onReasonSwitchCancel} onConfirm={onReasonSwitchConfirm} />
        </Styled.Container>
    );
};

export { SelectAppointmentPage };
